import { Dialog, Transition } from '@headlessui/react';
import { Download, ExternalLink, X, ZoomIn, ZoomOut } from 'lucide-react';

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { toast } from '@/utils/toast';

/**
 * Get file extension from MIME type
 */
function getExtensionFromMimeType(mimeType: string): string {
  const mimeToExt: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp',
    'image/tiff': 'tiff',
    'image/heic': 'heic',
    'image/heif': 'heif',
    'image/avif': 'avif',
    'application/pdf': 'pdf',
  };
  return mimeToExt[mimeType] || 'jpg';
}

interface PrefetchedImage {
  blobUrl: string;
  extension: string;
}

/**
 * Pre-fetches an image and returns a blob URL for downloading.
 * This is done ahead of time so the download can happen synchronously
 * when the user clicks, preserving the user gesture context.
 */
async function prefetchImageBlob(
  imageUrl: string
): Promise<PrefetchedImage | null> {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    return {
      blobUrl: window.URL.createObjectURL(blob),
      extension: getExtensionFromMimeType(blob.type),
    };
  } catch {
    return null;
  }
}

interface ExpenseReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Full-size original image URL for viewing */
  imageUrl: string;
  /** Thumbnail URL (for fallback) */
  thumbnailUrl?: string;
  kind?: 'image' | 'pdf';
}

/**
 * Full-screen modal for viewing expense receipt images. A PDF receipt is
 * embedded from its CDN URL instead, with an Open link and no zoom.
 * Features:
 * - Pinch-to-zoom on mobile
 * - Zoom controls on desktop
 * - Download button
 * - Close button
 */
export function ExpenseReceiptModal({
  isOpen,
  onClose,
  imageUrl,
  thumbnailUrl,
  kind = 'image',
}: ExpenseReceiptModalProps) {
  const { t } = useTranslation();
  useNamespace('expenses');
  useNamespace('common');

  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageError, setImageError] = useState(false);
  const [prefetchedImage, setPrefetchedImage] =
    useState<PrefetchedImage | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Pre-fetch the image blob when modal opens for reliable downloads
  useEffect(() => {
    if (!isOpen || !imageUrl) {
      return;
    }

    let cancelled = false;

    prefetchImageBlob(imageUrl).then(result => {
      if (!cancelled && result) {
        setPrefetchedImage(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, imageUrl]);

  // Reset state when modal opens/closes
  const handleClose = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageError(false);
    // Clean up blob URL to prevent memory leaks
    if (prefetchedImage) {
      window.URL.revokeObjectURL(prefetchedImage.blobUrl);
      setPrefetchedImage(null);
    }
    onClose();
  }, [onClose, prefetchedImage]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.5, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      // Reset position when zooming back to 1
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Mouse/touch drag for panning when zoomed
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      }
    },
    [zoom, position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoom > 1) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart, zoom]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch events for mobile pinch-to-zoom
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1 && zoom > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y,
        });
      }
    },
    [zoom, position]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1 && isDragging && zoom > 1) {
        setPosition({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart, zoom]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Double-click/tap to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (zoom === 1) {
      setZoom(2);
    } else {
      handleResetZoom();
    }
  }, [zoom, handleResetZoom]);

  // Download handler - uses pre-fetched blob for reliable downloads
  const handleDownload = useCallback(() => {
    const filename = `receipt-${Date.now()}`;

    // Use pre-fetched blob URL for synchronous download (preserves user gesture)
    if (prefetchedImage) {
      const link = document.createElement('a');
      link.href = prefetchedImage.blobUrl;
      link.download = `${filename}.${prefetchedImage.extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Fallback: open image in new tab if blob not ready
    // This lets the browser handle the download natively
    const newWindow = window.open(imageUrl, '_blank');
    if (!newWindow) {
      toast.error(t('expenses:photo.downloadError'));
    }
  }, [prefetchedImage, imageUrl, t]);

  // Image error handler - fallback to thumbnail
  const handleImageError = useCallback(() => {
    if (!imageError && thumbnailUrl) {
      setImageError(true);
    }
  }, [imageError, thumbnailUrl]);

  const displayUrl = imageError && thumbnailUrl ? thumbnailUrl : imageUrl;
  const pdf = kind === 'pdf';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/90" />
        </Transition.Child>

        <div className="fixed inset-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full h-full flex flex-col">
              {/* Header with controls */}
              <div className="flex items-center justify-between p-4 bg-black/50">
                <Dialog.Title className="text-white font-medium">
                  {t('expenses:photo.viewReceipt')}
                </Dialog.Title>

                <div className="flex items-center gap-2">
                  {pdf ? (
                    // iOS Safari shows only the first page inside a frame.
                    <a
                      href={imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-white hover:bg-white/20 transition-colors"
                    >
                      <ExternalLink aria-hidden="true" className="h-4 w-4" />
                      {t('expenses:photo.openReceipt')}
                    </a>
                  ) : (
                    <div className="hidden sm:flex items-center gap-1 mr-2">
                      <button
                        onClick={handleZoomOut}
                        disabled={zoom <= 1}
                        className="p-2 rounded-full text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label={t('common:upload.zoomOut')}
                      >
                        <ZoomOut className="h-5 w-5" />
                      </button>

                      <button
                        onClick={handleResetZoom}
                        className="px-2 py-1 rounded text-white text-sm hover:bg-white/20 transition-colors min-w-[60px]"
                      >
                        {Math.round(zoom * 100)}%
                      </button>

                      <button
                        onClick={handleZoomIn}
                        disabled={zoom >= 4}
                        className="p-2 rounded-full text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label={t('common:upload.zoomIn')}
                      >
                        <ZoomIn className="h-5 w-5" />
                      </button>
                    </div>
                  )}

                  {/* Download button - hidden on mobile/tablet due to poor UX */}
                  <button
                    onClick={handleDownload}
                    className="hidden md:block p-2 rounded-full text-white hover:bg-white/20 transition-colors"
                    aria-label={t('expenses:photo.download')}
                    title={t('expenses:photo.download')}
                  >
                    <Download className="h-5 w-5" />
                  </button>

                  {/* Close button */}
                  <button
                    onClick={handleClose}
                    className="p-2 rounded-full text-white hover:bg-white/20 transition-colors"
                    aria-label={t('common:actions.close')}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {pdf ? (
                <div className="flex-1 overflow-hidden">
                  <iframe
                    src={imageUrl}
                    title={t('expenses:photo.receipt')}
                    referrerPolicy="no-referrer"
                    className="h-[80vh] w-full"
                  />
                </div>
              ) : (
                <div
                  ref={containerRef}
                  className="flex-1 overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onDoubleClick={handleDoubleClick}
                >
                  <img
                    src={displayUrl}
                    alt={t('expenses:photo.receiptAlt')}
                    className="max-w-full max-h-full object-contain select-none"
                    style={{
                      transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                      transition: isDragging
                        ? 'none'
                        : 'transform 0.2s ease-out',
                    }}
                    onError={handleImageError}
                    draggable={false}
                  />
                </div>
              )}

              {pdf ? null : (
                <div className="sm:hidden p-3 text-center text-white/70 text-sm bg-black/50">
                  {t('expenses:photo.zoomHint')}
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
